import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography,
  Chip,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  GpsFixed as GpsFixedIcon,
  MyLocation as MyLocationIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { currentYear } from '../data/config';

// พิกัดมหาวิทยาลัยบูรพา
const BUU_COORDINATES = {
  lat: 13.2777,
  lng: 100.9254
};

// ฟังก์ชันแปลงระยะทางเป็นช่วง
const getDistanceRange = (distance) => {
  if (!distance) return { text: 'ไม่ทราบ', color: '#757575', bgcolor: '#f5f5f5' };
  
  if (distance < 50) return { text: '< 50 km', color: '#2e7d32', bgcolor: '#e8f5e9' };
  if (distance < 100) return { text: '50-100 km', color: '#388e3c', bgcolor: '#e8f5e9' };
  if (distance < 200) return { text: '100-200 km', color: '#689f38', bgcolor: '#f1f8e9' };
  if (distance < 300) return { text: '200-300 km', color: '#fbc02d', bgcolor: '#fffde7' };
  if (distance < 400) return { text: '300-400 km', color: '#ff8f00', bgcolor: '#fff8e1' };
  return { text: '> 400 km', color: '#d32f2f', bgcolor: '#ffebee' };
};

// ฟังก์ชันคำนวณระยะทาง (Haversine formula)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // รัศมีโลกในหน่วย km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
};

// ข้อมูลพิกัดจังหวัดในไทย
const provinceCoordinates = {
  'กระบี่': { lat: 8.0863, lng: 98.9063 },
  'กรุงเทพมหานคร': { lat: 13.7563, lng: 100.5018 },
  'กาญจนบุรี': { lat: 14.0227, lng: 99.5450 },
  'กาฬสินธุ์': { lat: 16.4322, lng: 103.5056 },
  'กำแพงเพชร': { lat: 16.4827, lng: 99.5225 },
  'ขอนแก่น': { lat: 16.4419, lng: 102.8359 },
  'จันทบุรี': { lat: 12.6103, lng: 102.1038 },
  'ฉะเชิงเทรา': { lat: 13.6904, lng: 101.0779 },
  'ชลบุรี': { lat: 13.3611, lng: 100.9847 },
  'ชัยนาท': { lat: 15.1853, lng: 100.1250 },
  'ชัยภูมิ': { lat: 15.8070, lng: 102.0313 },
  'ชุมพร': { lat: 10.4930, lng: 99.1800 },
  'เชียงราย': { lat: 19.9072, lng: 99.8407 },
  'เชียงใหม่': { lat: 18.7883, lng: 98.9853 },
  'ตรัง': { lat: 7.5563, lng: 99.6114 },
  'ตราด': { lat: 12.2431, lng: 102.5156 },
  'ตาก': { lat: 16.8697, lng: 99.1255 },
  'นครนายก': { lat: 14.2069, lng: 101.2130 },
  'นครปฐม': { lat: 13.8199, lng: 100.0620 },
  'นครพนม': { lat: 17.4081, lng: 104.7686 },
  'นครราชสีมา': { lat: 14.9799, lng: 102.0977 },
  'นครศรีธรรมราช': { lat: 8.4304, lng: 99.9631 },
  'นครสวรรค์': { lat: 15.7047, lng: 100.1373 },
  'นนทบุรี': { lat: 13.8621, lng: 100.5144 },
  'นราธิวาส': { lat: 6.4254, lng: 101.8253 },
  'น่าน': { lat: 18.7838, lng: 100.7792 },
  'บึงกาฬ': { lat: 18.3609, lng: 103.6464 },
  'บุรีรัมย์': { lat: 14.9930, lng: 103.1029 },
  'ปทุมธานี': { lat: 14.0208, lng: 100.5250 },
  'ประจวบคีรีขันธ์': { lat: 11.8126, lng: 99.7972 },
  'ปราจีนบุรี': { lat: 14.0504, lng: 101.3709 },
  'ปัตตานี': { lat: 6.8693, lng: 101.2503 },
  'พระนครศรีอยุธยา': { lat: 14.3692, lng: 100.5877 },
  'พะเยา': { lat: 19.1921, lng: 99.8956 },
  'พังงา': { lat: 8.4506, lng: 98.5347 },
  'พัทลุง': { lat: 7.6168, lng: 100.0745 },
  'พิจิตร': { lat: 16.4370, lng: 100.3488 },
  'พิษณุโลก': { lat: 16.8211, lng: 100.2659 },
  'เพชรบุรี': { lat: 13.1110, lng: 99.9398 },
  'เพชรบูรณ์': { lat: 16.4193, lng: 101.1409 },
  'แพร่': { lat: 18.1459, lng: 100.1401 },
  'ภูเก็ต': { lat: 7.8804, lng: 98.3923 },
  'มหาสารคาม': { lat: 16.1845, lng: 103.3007 },
  'มุกดาหาร': { lat: 16.5429, lng: 104.7238 },
  'แม่ฮ่องสอน': { lat: 19.3019, lng: 97.9659 },
  'ยโสธร': { lat: 15.7923, lng: 104.1459 },
  'ยะลา': { lat: 6.5411, lng: 101.2802 },
  'ร้อยเอ็ด': { lat: 16.0544, lng: 103.6531 },
  'ระนอง': { lat: 9.9638, lng: 98.6041 },
  'ระยอง': { lat: 12.6810, lng: 101.2815 },
  'ราชบุรี': { lat: 13.5282, lng: 99.8133 },
  'ลพบุรี': { lat: 14.7995, lng: 100.6533 },
  'ลำปาง': { lat: 18.2888, lng: 99.4904 },
  'ลำพูน': { lat: 18.5743, lng: 99.0108 },
  'เลย': { lat: 17.4860, lng: 101.7223 },
  'ศรีสะเกษ': { lat: 15.1186, lng: 104.3228 },
  'สกลนคร': { lat: 17.1547, lng: 104.1353 },
  'สงขลา': { lat: 7.2061, lng: 100.5959 },
  'สตูล': { lat: 6.6238, lng: 100.0673 },
  'สมุทรปราการ': { lat: 13.5991, lng: 100.5998 },
  'สมุทรสงคราม': { lat: 13.4078, lng: 100.0029 },
  'สมุทรสาคร': { lat: 13.5475, lng: 100.2745 },
  'สระแก้ว': { lat: 13.8242, lng: 102.0645 },
  'สระบุรี': { lat: 14.5289, lng: 100.9103 },
  'สิงห์บุรี': { lat: 14.8936, lng: 100.3968 },
  'สุโขทัย': { lat: 17.0126, lng: 99.8230 },
  'สุพรรณบุรี': { lat: 14.4745, lng: 100.1218 },
  'สุราษฎร์ธานี': { lat: 9.1382, lng: 99.3215 },
  'สุรินทร์': { lat: 14.8818, lng: 103.4936 },
  'หนองคาย': { lat: 17.8782, lng: 102.7412 },
  'หนองบัวลำภู': { lat: 17.2042, lng: 102.4280 },
  'อ่างทอง': { lat: 14.5896, lng: 100.4550 },
  'อำนาจเจริญ': { lat: 15.8651, lng: 104.6259 },
  'อุดรธานี': { lat: 17.4138, lng: 102.7870 },
  'อุตรดิตถ์': { lat: 17.6196, lng: 100.0992 },
  'อุทัยธานี': { lat: 15.3794, lng: 100.0245 },
  'อุบลราชธานี': { lat: 15.2286, lng: 104.8472 }
};

const CoopCompanyModal = ({ open, onClose, companies, onStudentClick }) => {
  const [sortBy, setSortBy] = useState('students'); // 'students', 'distance', 'province'
  const [searchTerm, setSearchTerm] = useState('');

  // กรองเฉพาะบริษัทที่รับสหกิจศึกษา และเรียงตามจำนวนนิสิต
  const coopCompanies = companies
    .filter(company => 
      company.internStudent?.some(student => 
        student.internDetail?.type_internship === "2" || 
        student.internDetail?.type_internship === "3"
      )
    )
    .map(company => {
      // นับจำนวนนิสิตสหกิจ
      const coopStudents = company.internStudent?.filter(student => 
        student.internDetail?.type_internship === "2" || 
        student.internDetail?.type_internship === "3"
      ) || [];
      
      // คำนวณระยะทางจากมหาวิทยาลัยบูรพา
      const provinceCoords = provinceCoordinates[company.province];
      const distance = provinceCoords ? 
        calculateDistance(
          BUU_COORDINATES.lat, 
          BUU_COORDINATES.lng, 
          provinceCoords.lat, 
          provinceCoords.lng
        ) : null;
      
      return {
        ...company,
        coopStudentCount: coopStudents.length,
        coopStudents,
        distance,
        isNearBUU: distance && distance <= 400
      };
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return (a.distance || 999999) - (b.distance || 999999);
        case 'province':
          return a.province.localeCompare(b.province);
        case 'students':
        default:
          return b.coopStudentCount - a.coopStudentCount;
      }
    })
    .filter(company => {
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      return (
        company.name.toLowerCase().includes(searchLower) ||
        company.id.toLowerCase().includes(searchLower) ||
        company.province.toLowerCase().includes(searchLower) ||
        company.address.toLowerCase().includes(searchLower) ||
        company.district.toLowerCase().includes(searchLower) ||
        company.subdistrict.toLowerCase().includes(searchLower)
      );
    });

  // คำนวณสถิติระยะทาง (ช่วงที่ไม่ซ้อนทับ)
  const distanceStats = {
    under50: coopCompanies.filter(c => c.distance && c.distance < 50).length,
    from50to100: coopCompanies.filter(c => c.distance && c.distance >= 50 && c.distance < 100).length,
    from100to200: coopCompanies.filter(c => c.distance && c.distance >= 100 && c.distance < 200).length,
    from200to300: coopCompanies.filter(c => c.distance && c.distance >= 200 && c.distance < 300).length,
    from300to400: coopCompanies.filter(c => c.distance && c.distance >= 300 && c.distance < 400).length,
    over400: coopCompanies.filter(c => c.distance && c.distance >= 400).length,
    unknown: coopCompanies.filter(c => !c.distance).length
  };

  const handleStudentClick = (company) => {
    if (onStudentClick) {
      onStudentClick(company);
    }
  };  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div">
            สถานประกอบการที่รับสหกิจศึกษา ปีการศึกษา {currentYear}
          </Typography>
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          ทั้งหมด {coopCompanies.length} สถานประกอบการ | รวม {coopCompanies.reduce((sum, c) => sum + c.coopStudentCount, 0)} นิสิตสหกิจศึกษา
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip 
            label={`< 50 km: ${distanceStats.under50}`} 
            size="small" 
            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
          />
          <Chip 
            label={`50-100 km: ${distanceStats.from50to100}`} 
            size="small" 
            sx={{ backgroundColor: '#e8f5e9', color: '#388e3c' }}
          />
          <Chip 
            label={`100-200 km: ${distanceStats.from100to200}`} 
            size="small" 
            sx={{ backgroundColor: '#f1f8e9', color: '#689f38' }}
          />
          <Chip 
            label={`200-300 km: ${distanceStats.from200to300}`} 
            size="small" 
            sx={{ backgroundColor: '#fffde7', color: '#fbc02d' }}
          />
          <Chip 
            label={`300-400 km: ${distanceStats.from300to400}`} 
            size="small" 
            sx={{ backgroundColor: '#fff8e1', color: '#ff8f00' }}
          />
          <Chip 
            label={`> 400 km: ${distanceStats.over400}`} 
            size="small" 
            sx={{ backgroundColor: '#ffebee', color: '#d32f2f' }}
          />
          {distanceStats.unknown > 0 && (
            <Chip 
              label={`ไม่ทราบ: ${distanceStats.unknown}`} 
              size="small" 
              sx={{ backgroundColor: '#f5f5f5', color: '#757575' }}
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={2}>
            <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
              <GpsFixedIcon sx={{ color: '#4caf50' }} />
              <Typography variant="body2" color="text.secondary">
                สถานประกอบการภายในรัศมี 400 km จากมหาวิทยาลัยบูรพา: {coopCompanies.filter(c => c.isNearBUU).length} แห่ง
              </Typography>
            </Box>
            
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                size="small"
                placeholder="ค้นหาบริษัท, รหัส, จังหวัด..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>เรียงลำดับตาม</InputLabel>
                <Select
                  value={sortBy}
                  label="เรียงลำดับตาม"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="students">จำนวนนิสิต (มาก-น้อย)</MenuItem>
                  <MenuItem value="distance">ระยะทาง (ใกล้-ไกล)</MenuItem>
                  <MenuItem value="province">จังหวัด (ก-ฮ)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {searchTerm && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              พบผลลัพธ์ {coopCompanies.length} รายการ จากการค้นหา "{searchTerm}"
            </Typography>
          )}
          
          <Divider />
        </Box>

        <TableContainer component={Paper} elevation={0}>
          <Table size="small" aria-label="coop company table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>อันดับ</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>รหัส</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>สถานประกอบการ</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>จังหวัด</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  จำนวนนิสิตสหกิจ
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  ระยะทาง
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coopCompanies.map((company, index) => (
                <TableRow 
                  key={company.id}
                  sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    '&:hover': { backgroundColor: '#e3f2fd' }
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={index + 1} 
                      size="small" 
                      color={index < 3 ? 'primary' : 'default'}
                      variant={index < 3 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={company.id}
                      size="small"
                      sx={{ 
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {company.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.address} ต.{company.subdistrict} อ.{company.district}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">
                        {company.province}
                      </Typography>
                      {company.isNearBUU && (
                        <GpsFixedIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="คลิกเพื่อดูรายชื่อนิสิต" placement="top">
                      <Chip 
                        label={`${company.coopStudentCount} คน`}
                        size="small"
                        onClick={() => handleStudentClick(company)}
                        sx={{ 
                          backgroundColor: '#ff9800',
                          color: 'white',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f57c00',
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {company.distance ? (
                      <Chip 
                        label={getDistanceRange(company.distance).text}
                        size="small"
                        sx={{ 
                          backgroundColor: getDistanceRange(company.distance).bgcolor,
                          color: getDistanceRange(company.distance).color,
                          fontWeight: 'bold',
                          border: `1px solid ${getDistanceRange(company.distance).color}20`
                        }}
                      />
                    ) : (
                      <Chip 
                        label="ไม่ทราบ"
                        size="small"
                        sx={{ 
                          backgroundColor: '#f5f5f5',
                          color: '#757575'
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
        >
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoopCompanyModal;
